import { useCallback } from 'react';

import { SlotActions } from '@Actions/SlotActions';
import { showNotification, NotificationType } from '@Components/design-system/notification/notification';
import { IQueryData, updateLocationCode } from '@Reducers/common';
import { IUserData } from '@Reducers/user';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { CONSULTATION_TYPE, DEFAULT_LOCATION_CODE, INSTANT_LOCATION_CODE } from '@Utils/constants';
import { isOfflineLocation } from '@Utils/helper';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';
import usePosthog, { CtaEvents } from '@Utils/hooks/posthog';

import { Direction } from '../constants';
import { fallsUnderInstant } from './initial';
import useOnEnter from './on-enter';
import { Rule } from './type';

export const useCityRule = (): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const locationIsLoading: boolean = useAppSelector((state) => state.location.isLoading);
  const userData: IUserData = useAppSelector((state) => state.user.userData);
  const { ctaEventCapture } = usePosthog();

  const next = useCallback(() => {
    ctaEventCapture(CtaEvents.CITY);
    switch (true) {
      case queryData?.locationCode === INSTANT_LOCATION_CODE:
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
        dispatch(SlotActions.ResetSlots());
        break;
      //if user has chosen online, go to the language selection page
      case queryData?.locationCode === DEFAULT_LOCATION_CODE &&
        (queryData?.consultationCode === CONSULTATION_TYPE.TH ? !userData?.therapistId : !userData?.physicianId):
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
        break;
      case queryData?.locationCode === DEFAULT_LOCATION_CODE &&
        (queryData?.consultationCode === CONSULTATION_TYPE.TH ? !!userData?.therapistId : !!userData?.physicianId):
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
        break;
      //if user has selected a city, go to the clinic selection page
      case !!queryData?.validUserCity:
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_CLINIC });
        break;
      default:
        showNotification(NotificationType.ERROR, 'Please select a city', 3000);
    }
  }, [router, queryData]);

  useOnEnter(() => {
    if (!queryData?.validUserCity) {
      dispatch(
        updateLocationCode(
          fallsUnderInstant() && queryData?.consultationCode === CONSULTATION_TYPE.SC && !queryData?.providerId?.[0]
            ? INSTANT_LOCATION_CODE
            : DEFAULT_LOCATION_CODE,
        ),
      );
    }
  }, [dispatch, queryData]);

  return {
    next: next,
    disableNext: !queryData?.validUserCity && isOfflineLocation(queryData?.locationCode),
    isLoading: locationIsLoading,
  };
};
